import { useTranslation } from 'react-i18next'
import { shallowEqual, useSelector } from 'react-redux'
import { FormikProvider } from 'formik'

import { Close, Check } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  MenuItem,
  Checkbox,
  IconButton,
} from '@mui/material'
import { brandsSelector } from '@/state/brands/slices/index.slices'

import { mapEnum } from '@/utils'
import { DialogsEnum } from '@/enums'
import { RoleType } from '@/enums/role.enum'

import { FormStatusType } from '@/enums/form-status.enum'
import { BrandsOptions, ListBrands } from '@/state/brands/models'
import { useUsersForm } from '@/views/users/hooks/use-users-form.hook'

import InputFormikField from '@/views/components/input-formik-field/index.component'
import InputFormikPassword from '@/views/components/input-formik-password/index.component'
import InputFormikAutocomplete from '@/views/components/input-formik-autocomplete/index.component'

export const UsersFormDialog = () => {
  const { t } = useTranslation()

  const { formMode, dialog, formikUser, handleSubmit, handleCloseDialog } =
    useUsersForm()

  const { brands } = useSelector(brandsSelector, shallowEqual)

  const roleOptions = mapEnum(RoleType, (index: RoleType) => {
    return (
      <MenuItem key={index} value={index}>
        <Checkbox
          checkedIcon={<Check />}
          checked={
            formikUser.values.role
              ? formikUser.values.role !== 0 && formikUser.values.role === index
              : false
          }
        />
        {t(`roleType.${RoleType[index as RoleType]}`)}
      </MenuItem>
    )
  })

  const brandsOptions: BrandsOptions[] =
    brands && brands.length > 0
      ? brands.map((brand: ListBrands) => {
          return {
            id: brand.id,
            name: brand.name,
            siteId: brand.siteId,
          }
        })
      : []

  const isCreating = formMode === FormStatusType.CREATE

  return (
    <Dialog
      open={dialog.open && dialog.id === DialogsEnum.DIALOG_USERS}
      aria-labelledby="dialog-title"
      className="form"
    >
      <DialogTitle id="dialog-title">
        {t(isCreating ? 'createNewUser' : 'editUser')}
        <IconButton aria-label="close" onClick={handleCloseDialog}>
          <Close />
        </IconButton>
      </DialogTitle>
      <FormikProvider value={formikUser}>
        <DialogContent>
          <Box overflow="hidden">
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <InputFormikField label={t('firstName')} name="firstName" />
              </Grid>
              <Grid item xs={6}>
                <InputFormikField label={t('lastName')} name="lastName" />
              </Grid>
              <Grid item xs={6}>
                <InputFormikField label={t('email')} name="email" />
              </Grid>
              <Grid item xs={6}>
                <InputFormikField
                  label={t('labels.username')}
                  name="username"
                />
              </Grid>
              <Grid item xs={6}>
                <InputFormikPassword
                  name="password"
                  label={t('labels.password')}
                />
              </Grid>
              <Grid item xs={6}>
                <InputFormikPassword
                  name="newPassword"
                  label={t('labels.confirmPassword')}
                />
              </Grid>
              <Grid item xs={12}>
                <InputFormikField
                  label={t('roles')}
                  name="role"
                  select
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      maxHeight: '16px',
                    },
                  }}
                >
                  {roleOptions}
                </InputFormikField>
              </Grid>
              <Grid item xs={12}>
                <InputFormikAutocomplete
                  name="brands"
                  label={t('brands')}
                  options={brandsOptions || []}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(
                    option: ListBrands,
                    value: ListBrands,
                  ) => option.id === value.id}
                  multiple
                  limitTags={2}
                  chipTags
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </FormikProvider>
      <DialogActions>
        <Button onClick={handleCloseDialog} className="red">
          {t('labels.cancel')}
        </Button>
        <Button
          disabled={!(formikUser.isValid && formikUser.dirty)}
          variant="contained"
          onClick={handleSubmit}
        >
          {isCreating ? t('labels.save') : t('labels.update')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
